'use client';

import { HTTP_REQUEST } from '@/lib/Constants';
import { Endpoints } from '@/lib/Endpoints';
import { fetchProductPrices, productIdsFromHomepageItems } from '@/lib/productPrices';
import { fetcher, Requester } from '@/lib/Requester';
import { BadgeInfo, CAMPAIGN_VIEW, HomePage, HomePageItem } from '@/models/HomePage';
import { useEffect, useState } from 'react';
import { DISCOUNT_TYPE, PriceData, Product } from '@/models/Product';
import Countdown from './Countdown';
import { parseISO, differenceInSeconds } from 'date-fns';
import { useRouter } from 'next/navigation';
import { ScreenSize, useScreenSize } from '@/lib/hook/useScreenSize';
import { GenericResponse } from '@/models/GenericResponse';
import { getReferralPromoCodes, setReferralPromoCodes } from '@/lib/Cookie';
import { trackEngagement } from '@/lib/engagement';
import { getIsAnonymous, isAppClient } from '@/lib/LocalStorage';
import dynamic from 'next/dynamic';
import SmartImage from './SmartImage';
import cdnLoader from '../../cdn-loader';

const ProductListSwiperPopup = dynamic(() => import('./ProductListSwiperPopup'), {
  ssr: false,
  loading: () => <div className="h-96 w-full animate-pulse bg-gray-200" />,
});

const ReferralCompletesDialog = dynamic(() => import('./modals/ReferralCompletesDialog'), {
  ssr: false,
});

export default function CampaignPopup() {
  const router = useRouter();
  const [visible, setVisible] = useState<Boolean>(false);
  const [badgeInfo, setBadgeInfo] = useState<BadgeInfo | null>(null);
  const [items, setItems] = useState<HomePageItem[] | undefined>(undefined);
  const [pricesData, setPricesData] = useState<PriceData | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [discount, setDiscount] = useState<string | null>();
  const [visibleProduct, setVisibleProduct] = useState<Product | null>(null);
  const screenSize = useScreenSize();
  const [newPromoCode, setNewPromoCode] = useState<string | null>(null);
  const [lcpCompleted, setLcpCompleted] = useState(false); // YENİ: LCP tracking
  const [hideForApp, setHideForApp] = useState(false);

  useEffect(() => {
    if (isAppClient()) setHideForApp(true);
  }, []);

  // Delay showing the popup until after load + 4s (perf). Fetch still starts on mount.
  useEffect(() => {
    let showTimer: ReturnType<typeof setTimeout> | undefined;
    const armShowGate = () => {
      showTimer = setTimeout(() => setLcpCompleted(true), 4000);
    };

    if (document.readyState === 'complete') {
      armShowGate();
    } else {
      window.addEventListener('load', armShowGate);
    }

    return () => {
      window.removeEventListener('load', armShowGate);
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  useEffect(() => {
    // Fire referral in parallel — never block /product/campaign on it.
    if (!getIsAnonymous()) {
      void (async () => {
        try {
          const { data: result } = await Requester.get<GenericResponse<any>>(
            Endpoints.GET_REFERRAL_PROMO_CODES,
          );
          if (result?.data?.referralPromoCodes?.length > 0) {
            const promo = result.data.referralPromoCodes[0]?.promoCode;
            const savedCodes = await getReferralPromoCodes();
            if (promo && !savedCodes.includes(promo)) {
              setNewPromoCode(promo);
            }
            setReferralPromoCodes(
              result.data.referralPromoCodes.map((code: any) => code.promoCode),
            );
          }
        } catch {
          // ignore
        }
      })();
    }

    const getCampaignData = async () => {
      try {
        const response = await fetcher<HomePage>({
          params: {},
          cacheKey: 'campaign_popup_refresh',
          endpoint: Endpoints.CAMPAIGN,
          method: HTTP_REQUEST.GET,
          cacheEnabled: false,
        });
        if (
          response?.homepage?.badge?.campaignViewType !== CAMPAIGN_VIEW.CENTER &&
          response?.homepage?.badge?.campaignViewType !== CAMPAIGN_VIEW.BOTTOM_RIGHT
        ) {
          setVisible(false);
          return;
        }
        if (response && response.homepage && response.homepage.items) {
          setItems(response.homepage.items);
          if (
            response.homepage.items &&
            response.homepage.items.length > 0 &&
            response.homepage.items[0].products &&
            response.homepage.items[0].products.length > 0 &&
            response.homepage.items[0].products[0].badge?.badgeEndTime
          ) {
            const badge = response.homepage.items[0].products[0].badge;

            // Warm shared prices cache before paint (deduped with homepage fetch).
            fetchProductPrices(productIdsFromHomepageItems(response.homepage.items)).then((data) =>
              setPricesData(data),
            );

            if (badge.badgeEndTime) {
              try {
                const badgeEndDate = parseISO(badge.badgeEndTime);
                if (differenceInSeconds(badgeEndDate, new Date()) > 10) {
                  setEndDate(badgeEndDate);
                }
              } catch (error) {
                console.error('Invalid date format for campaign badge:', badge);
              }
            }
            if (badge.discountType === DISCOUNT_TYPE.AMOUNT) {
              setDiscount(`-${badge.discountAmount}TL`);
            } else if (badge.discountType === DISCOUNT_TYPE.PERCENT) {
              setDiscount(`-%${badge.discountAmount}`);
            }
            if (response.homepage.badge) {
              setBadgeInfo(response.homepage.badge);
            }
            // Data is ready; visibility still waits for lcpCompleted.
            setVisible(true);
            return;
          }
          setVisible(false);
        }
      } catch {
        setVisible(false);
      }
    };

    void getCampaignData();
  }, []);

  // Show only after load+4s gate, and track once when actually shown.
  useEffect(() => {
    if (!lcpCompleted || !visible || !badgeInfo) return;
    const t = setTimeout(() => {
      trackEngagement({
        channel: 'onsite',
        event: 'viewed',
        campaignId: 'homepage_popup',
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [lcpCompleted, visible, badgeInfo]);

  useEffect(() => {
    const lockScroll = Boolean(visible && lcpCompleted);
    document.body.style.overflow = lockScroll ? 'hidden' : 'auto';
  }, [visible, lcpCompleted]);

  // YENİ: Popup görsellerini preload et
  useEffect(() => {
    if (visible && lcpCompleted && items?.[0]?.products?.[0]) {
      const firstProduct = items[0].products[0];
      if (firstProduct.imageUrl) {
        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'image';
        preloadLink.href = cdnLoader({ src: firstProduct.imageUrl, width: 278, quality: 85 });
        preloadLink.setAttribute('fetchpriority', 'low');
        document.head.appendChild(preloadLink);
      }
    }
  }, [visible, lcpCompleted, items]);

  if (hideForApp) {
    return null;
  }

  if (newPromoCode) {
    return <ReferralCompletesDialog code={newPromoCode} />;
  }

  // Fetch runs on mount; UI waits for the post-load gate.
  if (!visible || !lcpCompleted) {
    return null;
  }

  if (badgeInfo?.campaignViewType === CAMPAIGN_VIEW.BOTTOM_RIGHT) {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <div
          className="fixed right-2 bottom-20 z-50 w-full max-w-[610px] 
        gap-4 border bg-background p-[20px] 
        shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out 
        data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 
        data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 
        data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 
        data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg
        flex"
        >
          <div className="w-full max-w-[280px] h-[22rem]">
            <div className="w-full mb-28 self-stretch flex-grow flex flex-col justify-center px-[0] rounded-[12px] border-[solid] border-[1px] border-[#f1efe9] bg-[#fff] relative">
              {discount && (
                <div className="h-[48px] flex-grow-0 flex flex-row justify-center items-center gap-[10px] p-[10px] bg-[#2b3280] rounded-tl-none rounded-br-[12px] rounded-tr-[12px] rounded-bl-[12px] absolute top-[0] right-[0] z-10">
                  <span className="px-2 h-[26px] flex-grow-0 font-[Fraunces] text-[21px] font-bold [font-stretch:normal] not-italic leading-[normal] tracking-[-0.21px] text-center text-[#b4d633]">
                    {discount}
                  </span>
                </div>
              )}
              <ProductListSwiperPopup
                products={items?.[0].products}
                priceData={pricesData}
                inPopup={false}
                inTooltip={true}
                itemMaxWidth={570}
                carosuelWidth={570}
                imageWidth={278}
                setVisibleProduct={setVisibleProduct}
                hideCampaign={() => setVisible(false)}
              />
              <div className="flex flex-col items-center -mt-20 max-w-[280px]">
                {endDate && (
                  <Countdown
                    endDate={endDate}
                    scale="0.3"
                    transformOrigin="center"
                    cardColor="#2b3280"
                    textColor="#2b3280"
                    numberColor="#FFF"
                    hideHeader={true}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="w-full h-full grow-1 flex flex-col justify-between z-50">
            <div className="flex justify-between">
              <div>Günün Fırsatlarını Yakala!</div>
              <div className="cursor-pointer">
                <SmartImage
                  src="/close_line.png"
                  alt="Kapat"
                  width={20}
                  height={20}
                  loading="lazy"
                  onClick={() => {
                    setVisible(false);
                  }}
                  imageType="icon"
                  unoptimized
                />
              </div>
            </div>
          </div>
          <div className="absolute bottom-10 right-10 w-64">
            <div className="flex flex-col gap-2">
              {visibleProduct?.url && visibleProduct?.inStock !== 0 && (
                <div
                  className="self-stretch cursor-pointer text-[#FAFAF8] text-[14px] font-medium flex-grow-0 flex flex-row justify-center items-center gap-[8px] px-[16px] py-3 rounded-[999px] bg-[#2b3280]"
                  onClick={() => {
                    router.replace(visibleProduct?.url || '');
                    setVisible(false);
                  }}
                >
                  Sepete Ekle
                </div>
              )}
              {badgeInfo?.url && (
                <div
                  className="font-14 self-stretch cursor-pointer text-[#2b3280] text-[14px] font-medium flex-grow-0 flex flex-row justify-center items-center gap-[8px] px-[16px] py-3 rounded-[999px] bg-[rgba(94,_106,_255,_0.1)]"
                  onClick={() => {
                    router.replace(badgeInfo?.url || '');
                    setVisible(false);
                  }}
                >
                  Kampanyayı Keşfet
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  } else if (badgeInfo?.campaignViewType === CAMPAIGN_VIEW.CENTER) {
    return (
      <>
        <div className="fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-sm translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
          <div className="flex justify-between">
            <div>Günün Fırsatlarını Yakala!</div>
            <div className="cursor-pointer">
              <SmartImage
                src="/close_line.png"
                alt="Kapat"
                width={20}
                height={20}
                loading="lazy"
                onClick={() => {
                  setVisible(false);
                }}
                imageType="icon"
                unoptimized
              />
            </div>
          </div>
          <div className="w-full pb-32 self-stretch flex-grow flex flex-col justify-center items-center px-[0] rounded-[12px] border-[solid] border-[1px] border-[#f1efe9] bg-[#fff] relative">
            {discount && (
              <div className="h-[48px] flex-grow-0 flex flex-row justify-center items-center gap-[10px] p-[10px] bg-[#2b3280] rounded-tl-none rounded-br-[12px] rounded-tr-[12px] rounded-bl-[12px] absolute top-[0] right-[0] z-10">
                <span className="px-2 h-[26px] flex-grow-0 font-[Fraunces] text-[21px] font-bold [font-stretch:normal] not-italic leading-[normal] tracking-[-0.21px] text-center text-[#b4d633]">
                  {discount}
                </span>
              </div>
            )}
            <div className="max-w-xs">
              <ProductListSwiperPopup
                products={items?.[0].products}
                priceData={pricesData}
                inPopup={true}
                itemMaxWidth={264}
                hideCampaign={() => setVisible(false)}
              />
            </div>
            <div className="flex flex-col items-center h-20 -mt-[14.5rem] mb-[30px] max-w-[280px]">
              {/* <div className='w-[96px] ml-[100px] h-20 flex flex-col items-center'> */}
              {endDate && (
                <Countdown
                  endDate={endDate}
                  scale={screenSize === ScreenSize.SM ? '0.6' : '0.3'}
                  transformOrigin="center"
                  cardColor="#2b3280"
                  textColor="#2b3280"
                  numberColor="#FFF"
                  hideHeader={true}
                />
              )}
            </div>
          </div>
          {visibleProduct?.url && visibleProduct?.inStock !== 0 && (
            <div
              className="self-stretch cursor-pointer text-[#FAFAF8] text-[14px] font-medium flex-grow-0 flex flex-row justify-center items-center gap-[8px] px-[16px] py-3 rounded-[999px] bg-[#2b3280]"
              onClick={() => {
                router.replace(visibleProduct?.url || '');
                setVisible(false);
              }}
            >
              Sepete Ekle
            </div>
          )}
          {badgeInfo?.url && (
            <div
              className="font-14 self-stretch cursor-pointer text-[#2b3280] text-[14px] font-medium flex-grow-0 flex flex-row justify-center items-center gap-[8px] px-[16px] py-3 rounded-[999px] bg-[rgba(94,_106,_255,_0.1)]"
              onClick={() => {
                router.replace(badgeInfo?.url || '');
                setVisible(false);
              }}
            >
              Kampanyayı Keşfet
            </div>
          )}
        </div>
      </>
    );
  } else {
    return null;
  }
}
